<template>
  <div class="layout-inner job_home">
    <navbar></navbar>
    <div class="layout-container">
      <div class="layout-content">
        <template v-if="isLoading">
          <div class="w-100" style="padding: 150px 0;">
            <spinner size="large"></spinner>
          </div>
        </template>
        <template v-else>
          <div class="job-home-header">
            <div class="job-home-title">
              <span class="ic2_job-briefcase title-icon" aria-hidden="true"></span>
              <span>{{ job.title }}</span>
            </div>
            <div class="job-home-actions">
              <b-button
                v-if="!hasApplication"
                type="button"
                class="btn"
                @click="showApplyPopup">
                {{ $t('apply') }}
              </b-button>
              <span
                v-if="isBookmarkEnabled"
                class="ic_bm_container ace_sec_txt"
                :class="{ bookmarked: isJobBookmarked }"
                @click.stop="toggleJobBookmark">
                <span class="ic ic8-bookmark3"></span>
              </span>
            </div>
          </div>

          <div class="container-fluid flex-grow-1 pg_content_container job-home-body">
            <div class="job-section" v-if="hasApplication">
              <h2>{{ $t('applicationStatus') }}</h2>
              <div class="job-application-status">
                <template v-for="(stage, stageIndex) in statusStages">
                  <div
                    class="job-status-box"
                    :key="'detail-' + stage.key"
                    :class="{
                      'is-complete': isStageComplete(userApplication, stage),
                      'is-rejected': isStageRejected(userApplication, stage),
                      'has-offer-actions': canRespondToOffer(userApplication, stage)
                    }">
                    <template v-if="canRespondToOffer(userApplication, stage)">
                      <div class="offer-actions">
                        <button type="button" class="offer-btn accept" @click.stop="confirmOfferResponse('accept')">{{ $t('accept') }}</button>
                        <button type="button" class="offer-btn decline" @click.stop="confirmOfferResponse('decline')">{{ $t('decline') }}</button>
                      </div>
                    </template>
                    <template v-else>
                      <span class="job-status-label">{{ stageDisplayLabel(userApplication, stage) }}</span>
                      <span class="job-status-date">{{ stageDateLabel(userApplication, stage) }}</span>
                    </template>
                  </div>
                  <span
                    class="job-status-separator"
                    v-if="stageIndex < statusStages.length - 1"
                    :key="'sep-' + stage.key"
                    aria-hidden="true">&gt;</span>
                </template>
              </div>
            </div>

            <div class="job-section recommendation-row" v-if="hasRecommendation">
              <div
                class="recommendation-item"
                v-for="(recommendation, index) in userRecommendations"
                :key="recommendation.uuid || (recommendation.recommendedBy + '-' + recommendation.type + '-' + index)">
                <span class="ic icon-star-full"></span>
                {{ $t('thisJobRecommendedToYouBy') }} : {{ recommendation.recommendedBy }} ({{ recommendation.type }})
              </div>
            </div>

            <div class="job-meta-grid">
              <div class="job-meta-item job-salary-item">
                <span class="ic_salary meta-icon" aria-hidden="true"></span>
                <span class="salary-text" :title="job.salaryRange || $t('na')">{{ job.salaryRange || $t('na') }}</span>
              </div>
              <div class="job-meta-item">
                <span class="ic2_part-full-time-job meta-icon" aria-hidden="true"></span>
                <span>{{ jobTypeLabel }}</span>
              </div>
              <div class="job-meta-item">
                <span class="icon-location meta-icon" aria-hidden="true"></span>
                <span>{{ jobLocation }}</span>
              </div>
              <div class="job-meta-item">
                <span class="ic2_calendar meta-icon" aria-hidden="true"></span>
                <span>{{ $t('postedOn') }} {{ millisToDate(job.postedDate || job.creationTime) }}</span>
              </div>
              <div class="job-meta-item">
                <span class="ic2_applyBy meta-icon" aria-hidden="true"></span>
                <span>{{ $t('applyBy') }} {{ applyByLabel(job.applyBy) }}</span>
              </div>
              <div class="job-meta-item">
                <span class="ic22-eye meta-icon" aria-hidden="true"></span>
                <span>{{ job.viewCount || 0 }} {{ $t('views') }}</span>
              </div>
              <div class="job-meta-item">
                <span class="ic2_job-application meta-icon" aria-hidden="true"></span>
                <span>{{ applicantCountMessage(job) }}</span>
              </div>
            </div>

            <div class="job-section" v-if="hasJobTags">
              <h2>{{ $t('tags') }}</h2>
              <div class="tags_cat">
                <div class="tag" v-for="item in visibleJobTags" :key="item.key" @click.stop="onTagClick(item)">
                  <span class="tag_name" :class="item.nameClass">{{ item.name }}</span>
                  <span class="tag_lavel" v-if="item.level">{{ item.level }}</span>
                </div>
              </div>
              <b-button v-if="hasMoreJobTags" class="btn" @click="showTagsPopup">{{ $t('showAllTags') }}</b-button>
            </div>

            <div class="job-section">
              <h2>
                <span class="ic2_description meta-icon" aria-hidden="true"></span>
                {{ $t('jobDescription') }}
              </h2>
              <div class="job-description" v-if="job.description" v-html="job.description"></div>
              <div v-else>{{ $t('noDescriptionForJob') }}</div>
            </div>

            <div class="job-section" v-if="job.jobCategory">
              <h2>{{ $t('jobCategory') }}</h2>
              <div class="job-description" v-html="job.jobCategory"></div>
            </div>

            <div class="job-section">
              <h2>{{ $t('requirements') }}</h2>
              <div class="job-description" v-if="jobRequirement()" v-html="jobRequirement()"></div>
              <div v-else>{{ $t('noRequirementsForJob') }}</div>
            </div>

            <div class="job-section" v-if="jobExperience()">
              <h2>{{ $t('experienceRequired') }}</h2>
              <div class="job-description" v-html="jobExperience()"></div>
            </div>

            <div class="job-section">
              <h2>
                <span class="ic2_description meta-icon" aria-hidden="true"></span>
                {{ $t('contactInformation') }}
              </h2>
              <div class="contact-row">
                <span class="icon-user meta-icon" aria-hidden="true"></span>
                <span>{{ contact }}</span>
              </div>
              <div class="contact-row">
                <span class="ico-employer meta-icon" aria-hidden="true"></span>
                <span>{{ job.employer || $t('na') }}</span>
              </div>
              <div class="contact-row">
                <span class="icon-phone meta-icon" aria-hidden="true"></span>
                <span>{{ phone }}</span>
              </div>
              <div class="contact-row">
                <span class="ic-inst meta-icon" aria-hidden="true"></span>
                <span>{{ job.hiringFor ? job.hiringFor : jobLocation }}</span>
              </div>
              <div class="contact-row">
                <span class="ic_email1 meta-icon" aria-hidden="true"></span>
                <span>{{ email }}</span>
              </div>
              <div class="contact-row">
                <span class="ic2_url meta-icon" aria-hidden="true"></span>
                <a v-if="job.externalLink" :href="job.externalLink" target="_blank">{{ $t('moreInfo') }}</a>
                <span v-else>{{ $t('na') }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <b-modal ref="jobApplyPopup" :title="$t('applyForThisJob')" hide-header-close no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-10 hd_txt">{{ $t('applyForThisJob') }} : {{ job.title }}</div>
        <div class="col-2 btn_container">
          <button class="btn_close" :aria-label="$t('close')" @click="closeApplyPopup">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div>
        {{ $t('applyJobInstructionsPrefix') }}
        <a v-if="job.externalLink" :href="job.externalLink" target="_blank">{{ $t('here') }}</a>
        <span v-else>{{ $t('here') }}</span>.
        {{ $t('applyJobInstructionsSuffix') }}
      </div>
      <div slot="modal-footer" class="w-100">
        <b-button type="button" class="btn" @click="closeApplyPopup">{{ $t('cancel') }}</b-button>
        <b-button type="button" class="btn" @click="triggerAddJobApplication">{{ $t('markApplied') }}</b-button>
      </div>
    </b-modal>

    <b-modal ref="offerConfirmModal" no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-10 hd_txt">{{ $t('pleaseConfirm') }}</div>
        <div class="col-2 btn_container">
          <button class="btn_close" :aria-label="$t('close')" @click="cancelOfferResponse">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div>{{ offerConfirmMessage }}</div>
      <div slot="modal-footer" class="w-100">
        <b-button @click="confirmOfferResponseYes" class="btn float-right mr-2">{{ $t('yes') }}</b-button>
        <b-button @click="cancelOfferResponse" class="btn float-right mr-2">{{ $t('no') }}</b-button>
      </div>
    </b-modal>

    <b-modal ref="jobHomeTagsPopup" :title="$t('tags')" hide-header-close no-close-on-backdrop no-close-on-esc centered>
      <div slot="modal-header" class="row header-section w-100">
        <div class="col-10 hd_txt">{{ $t('tags') }}</div>
        <div class="col-2 btn_container">
          <button class="btn_close" :aria-label="$t('close')" @click="closeTagsPopup">
            <span class="ic-close"></span>
          </button>
        </div>
      </div>
      <div class="tags_cat">
        <div class="tag" v-for="item in jobTags" :key="item.key" @click.stop="onTagClick(item)">
          <span class="tag_name" :class="item.nameClass">{{ item.name }}</span>
          <span class="tag_lavel" v-if="item.level">{{ item.level }}</span>
        </div>
      </div>
      <div slot="modal-footer" class="w-100">
        <b-button type="button" class="btn" @click="closeTagsPopup">{{ $t('ok') }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { PACKAGE } from '../../../package_constants'
import Navbar from '../../misc/components/./Navbar'
import jobApplicationStatus from '../jobApplicationStatus'

export default {
  name: 'jobHome',
  mixins: [jobApplicationStatus],
  props: {
    uuid: {
      type: String,
      default: ''
    }
  },
  components: {
    Navbar
  },
  data () {
    return {
      isLoading: true,
      userApplication: {},
      userRecommendations: [],
      offerResponseSaving: false,
      pendingOfferAction: ''
    }
  },
  mounted () {
    this.loadPage()
  },
  watch: {
    uuid () {
      this.loadPage()
    }
  },
  methods: {
    ...mapActions([
      'loadJobDetail',
      'addJobActivity',
      'addJobApplication',
      'getJobApplication',
      'updateJobApplicationUserStatus',
      'loadRecommendationForEntity',
      'loadCreateBookmarks',
      'loadDeleteBookmark',
      'loadAllBookmarks'
    ]),
    loadPage () {
      this.isLoading = true
      this.loadJob()
      this.loadUserApplication()
      this.loadRecommendationForUser()
      this.loadAllBookmarks()
    },
    loadJob () {
      this.loadJobDetail(this.uuid).then(() => {
        this.triggerAddJobActivity()
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
      })
    },
    triggerAddJobActivity () {
      if (!this.job || !this.job.uuid || !this.userObj || !this.userObj.uuid) {
        return
      }
      this.addJobActivity({
        jobId: this.job.uuid,
        jobTitle: this.job.title,
        employerId: this.job.employerUuid,
        employerTitle: this.job.employer,
        institutionId: this.institution.uuid,
        institutionTitle: this.institution.title,
        userId: this.userObj.uuid,
        userFullName: ((this.userObj.firstName || '') + ' ' + (this.userObj.lastName || '')).trim(),
        type: 'View'
      })
    },
    applicantCountMessage (result) {
      let count = (result && result.applicantCount) || 0
      return count < 2 ? count + ' ' + this.$t('applicant') : count + ' ' + this.$t('applicants')
    },
    triggerAddJobApplication () {
      let jobApplication = {
        jobId: this.job.uuid,
        jobTitle: this.job.title,
        employerId: this.job.employerUuid,
        employerTitle: this.job.employer,
        employerLogo: this.job.thumbnail || this.job.thumbnailURI,
        institutionId: this.institution.uuid,
        institutionTitle: this.institution.title,
        institutionLogo: this.institution.logo,
        userId: this.userObj.uuid,
        userFullName: ((this.userObj.firstName || '') + ' ' + (this.userObj.lastName || '')).trim(),
        status: 'Applied'
      }
      this.addJobApplication(jobApplication).then(res => {
        if (res && res.status) {
          this.userApplication = res
        } else {
          this.userApplication = jobApplication
        }
        this.closeApplyPopup()
      })
    },
    loadUserApplication () {
      this.userApplication = {}
      if (!this.uuid) {
        return
      }
      this.getJobApplication(this.uuid).then(res => {
        if (res && res.status) {
          this.userApplication = res
        } else {
          this.userApplication = {}
        }
      })
    },
    loadRecommendationForUser () {
      this.userRecommendations = []
      if (!this.uuid) {
        return
      }
      this.loadRecommendationForEntity(this.uuid).then(res => {
        let recs = res
        if (res && res.data && (Array.isArray(res.data) || res.data.type || res.data.recommendedBy)) {
          recs = res.data
        }
        if (!Array.isArray(recs)) {
          recs = recs ? [recs] : []
        }
        this.userRecommendations = recs.filter(rec => rec && (rec.type || rec.recommendedBy))
      })
    },
    jobRequirement () {
      return this.job.educationQualification && this.job.educationQualification.length > 0 ? this.job.educationQualification[0] : ''
    },
    jobExperience () {
      return this.job.experienceRequired && this.job.experienceRequired.length > 0 ? this.job.experienceRequired[0] : ''
    },
    confirmOfferResponse (action) {
      this.pendingOfferAction = action
      this.$refs.offerConfirmModal.show()
    },
    cancelOfferResponse () {
      this.pendingOfferAction = ''
      this.$refs.offerConfirmModal.hide()
    },
    confirmOfferResponseYes () {
      let action = this.pendingOfferAction
      this.$refs.offerConfirmModal.hide()
      this.pendingOfferAction = ''
      this.respondToOffer(action)
    },
    respondToOffer (action) {
      let application = this.userApplication
      if (!application || this.offerResponseSaving) {
        return
      }
      let applicationId = application.uuid
      if (!applicationId) {
        return
      }
      let status = action === 'accept' ? 'Accepted' : 'Declined'
      this.offerResponseSaving = true
      this.updateJobApplicationUserStatus({ applicationId: applicationId, status: status }).then((response) => {
        this.offerResponseSaving = false
        let title = application.jobTitle || (this.job && this.job.title) || ''
        if (response) {
          this.applyOfferResponse(response)
          this.notifySuccess(this.$t('successfullyUpdatedApplication') + ' \'' + title + '\'')
        } else {
          this.notifyFailure(this.$t('unableToUpdateApplication') + ' \'' + title + '\'')
        }
      }).catch(() => {
        this.offerResponseSaving = false
        this.notifyFailure(this.$t('unableToUpdateApplication') + ' \'' + (application.jobTitle || (this.job && this.job.title) || '') + '\'')
      })
    },
    applyOfferResponse (response) {
      let updated = Object.assign({}, this.userApplication)
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        if (response.data && (response.data.status || response.data.uuid)) {
          updated = Object.assign(updated, response.data)
        } else {
          updated = Object.assign(updated, response)
        }
      }
      this.userApplication = updated
    },
    showTagsPopup () {
      this.$refs.jobHomeTagsPopup.show()
    },
    showApplyPopup () {
      this.$refs.jobApplyPopup.show()
    },
    closeApplyPopup () {
      this.$refs.jobApplyPopup.hide()
    },
    onTagClick (tag) {
      if (!tag || !tag.name) {
        return
      }
      this.closeTagsPopup()
      this.$router.push({
        name: 'jobBoard',
        query: { tag: tag.name }
      })
    },
    closeTagsPopup () {
      if (this.$refs.jobHomeTagsPopup) {
        this.$refs.jobHomeTagsPopup.hide()
      }
    },
    async toggleJobBookmark () {
      if (!this.job || !this.job.uuid) {
        return
      }
      if (this.isJobBookmarked) {
        await this.loadDeleteBookmark(this.currentBookmark.uuid)
        this.notifySuccess(this.$t('successfullyDeletedBookmark') + ' \'' + this.job.title + '\'')
      } else {
        const bookmark = {
          'source': 'explore',
          'entityType': 'job',
          'entityTitle': this.job.title,
          'entityUuid': this.job.uuid,
          'institutionId': this.job.employerId || this.job.employerUuid,
          'userId': this.userObj.uuid
        }
        const response = await this.loadCreateBookmarks(bookmark)
        const message = (response ? this.$t('successfullyBookmarked') : this.$t('unableToBookmark')) + ' \'' + this.job.title + '\''
        if (response) {
          this.notifySuccess(message)
        } else {
          this.notifyFailure(message)
        }
      }
      await this.loadAllBookmarks()
    },
    notifySuccess (text) {
      return this.$notify({
        group: 'notify',
        type: 'success',
        title: '',
        text: text
      })
    },
    notifyFailure (text) {
      return this.$notify({
        group: 'notify',
        type: 'error',
        title: '',
        text: text
      })
    }
  },
  computed: {
    ...mapState({
      'job': state => state.job.job,
      'institution': state => state.dashboard.institution,
      'userObj': state => state.login.userObj,
      'bookmarks': state => state.explore.bookmarks,
      'enabledFeatures': state => state.dashboard.enabledFeatures
    }),
    jobTypeLabel () {
      if (this.job && this.job.jobTypes && this.job.jobTypes.length > 0) {
        return this.job.jobTypes[0]
      }
      return this.$t('na')
    },
    jobLocation () {
      if (this.job && this.job.contactInfo && this.job.contactInfo.addresses && this.job.contactInfo.addresses.length > 0) {
        let address = this.job.contactInfo.addresses[0]
        let city = address.city || ''
        let state = address.state || ''
        if (city && state) {
          return city + ', ' + state
        }
        return city || state || this.$t('na')
      }
      return this.$t('na')
    },
    jobTags () {
      let tags = []
      if (!this.job) {
        return tags
      }
      let appliedTagMap = this.job.appliedTagMap
      if (appliedTagMap) {
        Object.keys(appliedTagMap).forEach((key) => {
          let values = appliedTagMap[key]
          if (values && values.length) {
            for (let i = 0; i < values.length; i++) {
              let tag = values[i]
              tags.push({
                key: 'applied-' + (tag.uuid || tag.name || i),
                name: tag.name,
                category: key,
                kind: 'applied',
                nameClass: 'instructional_tag',
                cssClass: '',
                level: ''
              })
            }
          }
        })
      }
      let competencies = this.job.careerCompetenciesList
      if (competencies && competencies.length) {
        for (let i = 0; i < competencies.length; i++) {
          let tag = competencies[i]
          let level = ''
          if (tag.fulfillLevels && tag.fulfillLevels.length > 0) {
            level = tag.fulfillLevels[0]
          }
          tags.push({
            key: 'competency-' + (tag.uuid || tag.name || i),
            name: tag.name,
            category: tag.tagType || '',
            kind: 'competency',
            nameClass: 'competency_tag',
            cssClass: tag.tagType || '',
            level: level
          })
        }
      }
      return tags
    },
    visibleJobTags () {
      return this.jobTags.slice(0, 3)
    },
    hasMoreJobTags () {
      return this.jobTags.length > 3
    },
    hasJobTags () {
      return this.jobTags.length > 0
    },
    contact () {
      if (this.job.contactInfo && this.job.contactInfo.contactsList && this.job.contactInfo.contactsList.length > 0) {
        var contact = ''
        if (this.job.contactInfo.contactsList[0].name) {
          contact = this.job.contactInfo.contactsList[0].name
        }
        if (this.job.contactInfo.contactsList[0].title) {
          contact = contact ? (contact + ', ' + this.job.contactInfo.contactsList[0].title) : this.job.contactInfo.contactsList[0].title
        }
        return contact || this.$t('na')
      }
      return this.$t('na')
    },
    phone () {
      if (this.job.contactInfo && this.job.contactInfo.phoneList && this.job.contactInfo.phoneList.length > 0) {
        return this.job.contactInfo.phoneList[0].value
      }
      return this.$t('na')
    },
    email () {
      if (this.job.contactInfo && this.job.contactInfo.eMailList && this.job.contactInfo.eMailList.length > 0) {
        return this.job.contactInfo.eMailList[0].value
      }
      return this.$t('na')
    },
    hasApplication () {
      return !!(this.userApplication && this.userApplication.status)
    },
    offerConfirmMessage () {
      if (this.pendingOfferAction === 'decline') {
        return this.$t('declineOfferConfirm')
      }
      return this.$t('acceptOfferConfirm')
    },
    hasRecommendation () {
      return this.userRecommendations && this.userRecommendations.length > 0
    },
    isBookmarkEnabled () {
      let bookmarkFeature = this.enabledFeatures && this.enabledFeatures[PACKAGE.FEATURE_IDS.BOOKMARK_FEATURE_ID]
      return !!(bookmarkFeature && bookmarkFeature.enabled)
    },
    currentBookmark () {
      if (!this.job || !this.job.uuid || !this.bookmarks || !this.bookmarks.length) {
        return null
      }
      return this.bookmarks.find((bookmark) => {
        return bookmark.entityUuid === this.job.uuid
      }) || null
    },
    isJobBookmarked () {
      return !!this.currentBookmark
    }
  }
}
</script>

<style scoped>
  .job-home-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #e6e6e6;
  }
  .job-home-title {
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    margin-right: 12px;
  }
  .job-home-title .title-icon {
    margin-right: 8px;
    font-size: 22px;
    color: #4a4a4a;
    flex-shrink: 0;
  }
  .job-home-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .job-home-body {
    padding: 16px;
  }
  .job-section {
    margin-bottom: 20px;
  }
  .job-section h2 {
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-bottom: 8px;
  }
  .job-meta-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 12px;
    margin-bottom: 20px;
  }
  .job-meta-item,
  .contact-row {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 13px;
    color: #4a4a4a;
  }
  .meta-icon {
    margin-right: 8px;
    flex-shrink: 0;
    font-size: 16px;
    color: #4a4a4a;
    line-height: 1;
  }
  .job-section h2 .meta-icon {
    font-size: 18px;
  }
  .contact-row .ic-inst {
    font-size: 16px !important;
    margin-top: 0;
    color: #4a4a4a;
  }
  .contact-row .ico-employer {
    font-size: 16px;
    color: #91278e;
  }
  .job-application-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }
  .job-status-separator {
    display: inline-flex;
    align-items: center;
    line-height: 1;
    color: #6b6b6b;
  }
  .job-status-box {
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    padding: 8px;
    min-width: 90px;
    background: #f7f7f7;
  }
  .job-status-box.is-complete {
    background: #e6f4ea;
    border-color: #67a879;
  }
  .job-status-box.is-rejected {
    background: #fdecea;
    border-color: #d9534f;
  }
  .job-status-label,
  .job-status-date {
    display: block;
    font-size: 12px;
  }
  .job-status-box.has-offer-actions {
    padding: 6px;
    min-width: 90px;
    max-width: 110px;
  }
  .offer-actions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }
  .offer-btn {
    border: none;
    border-radius: 6px;
    padding: 4px 6px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    width: 100%;
    white-space: nowrap;
    color: #fff;
  }
  .offer-btn.accept {
    background: #4caf50;
    color: #fff;
  }
  .offer-btn.decline {
    background: #e53935;
    color: #fff;
  }
  .tags_cat {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .tag {
    background: #eef3f7;
    border-radius: 12px;
    padding: 4px 8px;
    font-size: 12px;
  }
  .ic_bm_container.bookmarked {
    color: #3d5a99;
  }
  .contact-row {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-bottom: 6px;
  }
  .job-salary-item {
    min-width: 0;
    overflow: hidden;
    max-width: 100%;
  }
  .job-salary-item .salary-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .recommendation-row {
    background: #fff8dc;
    padding: 6px 12px;
    border-radius: 6px;
  }
  .recommendation-item {
    margin-bottom: 8px;
  }
  .recommendation-row .recommendation-item:last-child {
    margin-bottom: 0;
  }
</style>



// WEBPACK FOOTER //
// src/app/job/components/JobHome.vue